// src/Redux/Slices/permissionsSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getProfileData } from '../../service/Users/usersService';

export type PermissionAction = 'view' | 'create' | 'update' | 'delete';

export type NotificationFcmScope = 'user' | 'store';

interface Role {
  roleID: number;
  name: string;
  description: string;
  status: boolean;
  permissions: Record<string, PermissionAction[]>;
  loading: string;
  error: string | null;
  profileStoreId:[];
  phone_number: string;
  email: string;
  notification_scope: NotificationFcmScope | null;
}

const initialState: Role = {
  roleID: 0,
  name: '',
  description: '',
  status: false,
  permissions: {},
  loading: "pending",
  error: null,
  profileStoreId:[],
  phone_number: '',
  email: '',
  notification_scope: null,
};

// Async thunk to fetch role by profile
export const fetchRoles:any = createAsyncThunk(
  'permissions/fetchRole',
  async (_,{ rejectWithValue }) => {
    try {
      const response = await getProfileData();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Something went wrong');
    }
  }
);

// Permissions slice
const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    setPermissions(
      state,
      action: PayloadAction<{
        roleID: number;
        name: string;
        description: string;
        status: boolean;
        permissions: Record<string, PermissionAction[]>;
        notification_scope?: NotificationFcmScope | null;
      }>
    ) {
      const { roleID, name, description, status, permissions, notification_scope } = action.payload;
      state.roleID = roleID;
      state.name = name;
      state.description = description;
      state.status = status;
      state.permissions = permissions;
      state.notification_scope =
        notification_scope === 'user' || notification_scope === 'store'
          ? notification_scope
          : null;
    },
    clearPermissions(state) {
      state.roleID = 0;
      state.name = '';
      state.description = '';
      state.status = false;
      state.permissions = {};
      state.loading = "pending";
      state.error = null;
      state.phone_number = '';
      state.email = '';
      state.notification_scope = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        const { id, name, description, status, permissions } = action.payload.role;
        state.roleID = id;
        state.name = name;
        state.description = description;
        state.status = status;
        state.permissions = permissions;
        state.loading = "fulfilled";
        // state.loading = false;
        state.profileStoreId=action.payload.stores;
        state.phone_number = action.payload.phone_number || '';
        state.email = action.payload.email || '';
        const apiScope = action.payload.notification_scope;
        state.notification_scope =
          apiScope === 'user' || apiScope === 'store' ? apiScope : null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
      });
  },
});

export const { setPermissions, clearPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;
